import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  CircularProgress,
  Popover,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  MarkEmailRead as MarkEmailReadIcon,
  AccountCircle as AccountCircleIcon,
  MeetingRoom as MeetingIcon,
  Logout as LogoutSessionIcon,
  PaletteOutlined as PaletteOutlinedIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Profile_page, NOTIFICATIONS_PATH, DASHBOARD_PATH } from '../navigations/path';
import {
  getNotifications,
  markNotificationAsRead,
  prependUnreadNotification,
  removeUnreadNotification,
} from '../store/slices/userSlice';
import { toast } from 'react-toastify';
import ThemeSettingsPanel from './theme/ThemeSettingsPanel';
import { subscribeToUserNotifications } from '../services/pusherClient';

const Navbar = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { user, logout, impersonating, leaveImpersonation, loading: authLoading } = useAuth();
  const [userAnchorEl, setUserAnchorEl] = useState(null);
  const [leaveImpersonationBusy, setLeaveImpersonationBusy] = useState(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const notifications = useSelector((state) => state.users.unreadNotifications ?? []);
  const totalUnread = useSelector((state) => state.users.totalUnread ?? 0);
  const unreadLoading = useSelector((state) => state.users.unreadLoading);
  const [themeAnchorEl, setThemeAnchorEl] = useState(null);
  const navigate = useNavigate();

  // Fetch unread once into Redux; remounts skip network while hydrated.
  useEffect(() => {
    if (!user?.id) return;
    dispatch(getNotifications());
  }, [dispatch, user?.id]);

  // Real-time notifications via Pusher — push new notification into the bell
  // dropdown and bump the unread count as soon as the backend broadcasts it.
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = subscribeToUserNotifications(user.id, (payload) => {
      const incoming = payload?.data ?? payload;
      const notification = {
        id: payload?.id ?? incoming?.id,
        data: incoming,
        created_at: payload?.created_at ?? new Date().toISOString(),
      };

      dispatch(prependUnreadNotification(notification));

      const toastTitle = incoming?.title || 'New Notification';
      const toastMessage = incoming?.message || 'You have a new notification';
      toast.info(
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {toastTitle}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.95 }}>
            {toastMessage}
          </Typography>
        </Box>,
        { autoClose: 2500 }
      );
    });

    return unsubscribe;
  }, [user?.id, dispatch]);

  // User menu handlers
  const handleUserMenuOpen = (event) => {
    setUserAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserAnchorEl(null);
  };

  // Notification menu handlers — force refresh when opening the dropdown
  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    dispatch(getNotifications({ force: true }));
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
  };

  const handleLeaveImpersonation = async () => {
    if (authLoading) return;
    setLeaveImpersonationBusy(true);
    try {
      await leaveImpersonation();
      handleUserMenuClose();
      navigate(DASHBOARD_PATH, { replace: true });
    } catch {
      // toast in auth slice
    } finally {
      setLeaveImpersonationBusy(false);
    }
  };

  const handleProfileClick = () => {
    navigate(Profile_page);
    handleUserMenuClose();
  };

  // UPDATED: Mark single notification as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      await dispatch(markNotificationAsRead(notificationId)).unwrap();
      dispatch(removeUnreadNotification(notificationId));
      toast.success('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark notification as read');
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    return user.name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Format notification time
  const formatTime = (dateString) => {
    if (!dateString) return 'Recently';

    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return diffInMinutes < 1 ? 'Just now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return `${Math.floor(diffInHours / 24)}d ago`;
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (notificationType) => {
    switch (notificationType) {
      case 'meeting_scheduled':
        return <MeetingIcon sx={{ color: 'primary.main', fontSize: 20 }} />;
      default:
        return <EmailIcon sx={{ color: 'primary.main', fontSize: 20 }} />;
    }
  };

  // UPDATED: Handle notification click with mark as read and navigation to lead detail
  const handleNotificationClick = async (notification) => {
    try {
      // First mark the notification as read
      await dispatch(markNotificationAsRead(notification.id)).unwrap();
      dispatch(removeUnreadNotification(notification.id));

      // Extract UUID from the URL and navigate to lead detail
      if (notification.data?.url) {
        const url = notification.data.url;

        // Check if it's a lead URL and extract UUID
        if (url.includes('/leads/')) {
          // Extract the UUID from the URL
          const urlParts = url.split('/');
          const leadUuid = urlParts[urlParts.length - 1];


          // This matches your route: /leads/view/:id
          navigate(`/leads/view/${leadUuid}`);
        } else if (url.includes('/issues/')) {
          const urlParts = url.split('/');
          const leadUuid = urlParts[urlParts.length - 1];

          navigate(`/issues/view/${leadUuid}`);
        }

        else {
          // If it's not a lead URL, you can handle other types of notifications here
          // You might want to navigate to a default page or show a message
          // navigate('/dashboard');
        }
      } else {
      }

      handleNotificationMenuClose();
    } catch (error) {
      console.error('Error handling notification click:', error);
      toast.error('Failed to process notification');
    }
  };

  const impersonationBannerH = 40;
  const headerTop = impersonating ? impersonationBannerH + 4 : 0;

  return (
    <Box>
      {impersonating && (
        <Box
          role="status"
          aria-live="polite"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1002,
            minHeight: impersonationBannerH,
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            bgcolor: 'warning.dark',
            color: 'warning.contrastText',
            borderBottom: 1,
            borderColor: 'warning.main',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, pr: 1 }} noWrap>
            Viewing as <strong>{user?.name || 'user'}</strong> (impersonation)
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            disabled={authLoading || leaveImpersonationBusy}
            onClick={handleLeaveImpersonation}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'rgba(255,255,255,0.8)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(0,0,0,0.12)' },
            }}
          >
            {leaveImpersonationBusy ? 'Switching…' : 'Back to my account'}
          </Button>
        </Box>
      )}

      {/* Top Orange Strip */}
      <Box
        sx={
          impersonating
            ? {
                position: 'fixed',
                top: impersonationBannerH,
                left: 0,
                right: 0,
                zIndex: 1000,
                height: 4,
                bgcolor: theme.brand?.accent ?? '#FF8C42',
                width: '100%',
              }
            : {
                height: 4,
                bgcolor: theme.brand?.accent ?? '#FF8C42',
                width: '100%',
              }
        }
      />

      {/* Main Header */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          px: 3,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'fixed',
          top: headerTop,
          left: 0,
          right: 0,
          zIndex: 1000,
          boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 8px rgba(15,23,42,0.06)',
        }}
      >
        {/* Logo — intrinsic width + left align (avoid width:100% stretching across flex:1 when sidebar opens) */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            justifyContent: 'flex-start',
            minWidth: 0,
          }}
        >
          <Box
            component="a"
            href="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              lineHeight: 0,
            }}
          >
            <Box
              component="img"
              src="/images/revive-portal-logo.png"
              alt="Revive Portal"
              sx={{
                height: { xs: 48, sm: 56 },
                width: 'auto',
                maxWidth: { xs: 180, sm: 220 },
                display: 'block',
                objectFit: 'contain',
              }}
            />
          </Box>
        </Box>

        {/* Right Side - Notifications and User Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Appearance">
            <IconButton
              onClick={(e) => setThemeAnchorEl(e.currentTarget)}
              sx={{
                color: 'primary.main',
                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
              }}
            >
              <PaletteOutlinedIcon />
            </IconButton>
          </Tooltip>

          {/* Notifications Bell Icon */}
          <IconButton
            onClick={handleNotificationMenuOpen}
            sx={{
              color: 'primary.main',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Badge
              badgeContent={totalUnread}
              color="error"
              max={99}
            >
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {/* User Info with Avatar and Name */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: 1,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
            onClick={handleUserMenuOpen}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {getUserInitials()}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {user?.name || 'User'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
              }}
            >
            (  {user?.roles?.[0]?.name })
            </Typography>
          </Box>
        </Box>

        {/* Notifications Dropdown Menu */}
        <Menu
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          onClose={handleNotificationMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: 360,
              maxHeight: 480,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              mt: 1
            }
          }}
        >
          {/* Notifications Header */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Notifications
                {totalUnread > 0 && (
                  <Typography
                    component="span"
                    sx={{
                      ml: 1,
                      color: 'error.main',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}
                  >
                    ({totalUnread} unread)
                  </Typography>
                )}
              </Typography>
              {notifications.length > 0 && (
                <Button
                  size="small"
                  startIcon={<MarkEmailReadIcon />}
                  // onClick={handleMarkAllAsRead}
                  sx={{
                    color: 'primary.main',
                    fontSize: '0.75rem',
                    textTransform: 'none',
                  }}
                >
                  Mark all as read
                </Button>
              )}
            </Box>
          </Box>

          {/* Notifications List */}
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            {unreadLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} color="primary" />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <NotificationsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No new notifications
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {notifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 0 },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getNotificationIcon(notification.data?.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {notification.data?.title || 'New Notification'}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {notification.data?.message || 'You have a new notification'}
                          </Typography>
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                            {formatTime(notification.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification.id);
                      }}
                      sx={{ color: 'grey.500' }}
                    >
                      <MarkEmailReadIcon fontSize="small" />
                    </IconButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {/* Notifications Footer */}
          <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Button
              size="small"
              onClick={() => {
                handleNotificationMenuClose();
                navigate(NOTIFICATIONS_PATH);
              }}
              sx={{
                color: 'primary.main',
                fontSize: '0.75rem',
                textTransform: 'none',
              }}
            >
              View all notifications
            </Button>
          </Box>
        </Menu>

        {/* User Dropdown Menu */}
        <Menu
          anchorEl={userAnchorEl}
          open={Boolean(userAnchorEl)}
          onClose={handleUserMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              width: 200,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              mt: 1
            }
          }}
        >
          {/* User Info in Dropdown */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600 }}>
              {user?.name || 'User'}
            </Typography>
            <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 400 }}>
              ({user?.roles?.[0]?.name || 'No Role'})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email || 'user@example.com'}
            </Typography>
          </Box>

          <MenuItem onClick={handleProfileClick}>
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" sx={{ color: 'primary.main' }} />
            </ListItemIcon>
            <Typography variant="body2">My Profile</Typography>
          </MenuItem>

          {impersonating && [
            <Divider key="imp-div" />,
            <MenuItem
              key="imp-leave"
              onClick={handleLeaveImpersonation}
              disabled={authLoading || leaveImpersonationBusy}
            >
              <ListItemIcon>
                <LogoutSessionIcon fontSize="small" sx={{ color: 'warning.main' }} />
              </ListItemIcon>
              <Typography variant="body2" color="warning.dark" fontWeight={600}>
                {leaveImpersonationBusy ? 'Switching account…' : 'Back to my account'}
              </Typography>
            </MenuItem>,
          ]}

          <Divider />

          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <PersonIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            <Typography variant="body2" color="error">Logout</Typography>
          </MenuItem>
        </Menu>

        <Popover
          open={Boolean(themeAnchorEl)}
          anchorEl={themeAnchorEl}
          onClose={() => setThemeAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              p: 2,
              width: 340,
              maxWidth: '95vw',
              borderRadius: 2,
            },
          }}
        >
          <ThemeSettingsPanel compact />
        </Popover>
      </Box>
    </Box>
  );
};

export default Navbar;